import { Component, ViewChild, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, finalize, map, of } from 'rxjs';

import { OrdersService } from '../../../core/services/order/order.service';
import { OrderTimelineService } from '../../../core/services/order-timeline/order-timeline.service';
import {
  DEFAULT_REPROCESS_INFO,
  KruzerOrderTimelineEvent,
  OrderAlert,
  OrderReprocessInfo,
  OrderTimelineEvent,
} from '../../../core/services/order-timeline/order-timeline-event.model';
import { WorkflowOrderService } from '../../../core/services/workflow-order/workflow-order.service';
import { AuthenticationService } from '../../../core/services/authentication/authentication.service';
import { WorkflowUserApproverModel } from '../../../core/models/workflow/workflow-order.model';
import { ROLE_VTEX_ORDERS_WRITE } from '../../../core/models/user/user-info.model';
import { mapOrderV2ApiToData } from './order-v2.mapper';
import { OrderV2Data, TimelineEvent } from './order-v2.types';

import { LoadingComponent } from '../../../shared/components/loading/loading.component';
import { AlertComponent } from '../../../shared/components/alert/alert.component';
import { ModalComponent } from '../../../shared/components/modal/modal.component';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { ApprovalWorkflowComponent } from '../../../shared/components/approval-workflow/approval-workflow.component';

import { OrderHeaderComponent } from './components/order-header/order-header.component';
import { RealtimeJsonModalComponent } from './components/realtime-json-modal/realtime-json-modal.component';
import { IntegrationAlertsComponent } from './components/integration-alerts/integration-alerts.component';
import { OrderTimelineComponent } from './components/order-timeline/order-timeline.component';
import { OrderItemsComponent } from './components/order-items/order-items.component';
import { LogisticsSectionComponent } from './components/logistics-section/logistics-section.component';

import { FinancialSummaryComponent } from './components/financial-summary/financial-summary.component';
import { PaymentsListComponent } from './components/payments-list/payments-list.component';
import { CustomerCardComponent } from './components/customer-card/customer-card.component';
import { PartnerCardComponent } from './components/partner-card/partner-card.component';
import { SellerCardComponent } from './components/seller-card/seller-card.component';

const HOOK_OCCURRED = new Set(['Tuna', 'Vtex', 'Conectala', 'Anymarket']);
const HOOK_EXPECTED = new Set(['Tuna', 'Vtex', 'Conectala']);
const PAYMENT_APPROVED_DESC = 'pagamento aprovado / pronto para manuseio';
const KRUZER_LABEL = 'Krz';

function displaySystemName(name: string): string {
  return name === 'Kruzer' ? KRUZER_LABEL : name;
}

@Component({
  selector: 'app-order-v2',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LoadingComponent,
    AlertComponent,
    ModalComponent,
    ButtonComponent,
    ApprovalWorkflowComponent,
    OrderHeaderComponent,
    RealtimeJsonModalComponent,
    IntegrationAlertsComponent,
    OrderTimelineComponent,
    OrderItemsComponent,
    LogisticsSectionComponent,
    FinancialSummaryComponent,
    PaymentsListComponent,
    CustomerCardComponent,
    PartnerCardComponent,
    SellerCardComponent,
  ],
  templateUrl: './order-v2.component.html',
  styleUrl: './order-v2.component.scss',
})
export class OrderV2Component {
  @ViewChild('reprocessModal') reprocessModal!: ModalComponent;
  @ViewChild('realtimeModal') realtimeModal!: RealtimeJsonModalComponent;

  private readonly route = inject(ActivatedRoute);
  private readonly ordersService = inject(OrdersService);
  private readonly orderTimelineService = inject(OrderTimelineService);
  private readonly workflowOrderService = inject(WorkflowOrderService);
  private readonly authService = inject(AuthenticationService);

  readonly routeOrderId = toSignal(
    this.route.paramMap.pipe(map((p) => (p.get('orderId') ?? '').trim())),
    { initialValue: '' }
  );

  readonly data = signal<OrderV2Data | null>(null);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly successMsg = signal<string | null>(null);

  readonly timeline = signal<TimelineEvent[]>([]);
  readonly kruzerTimeline = signal<TimelineEvent[]>([]);
  readonly timelineLoading = signal(false);

  readonly reprocess = signal<OrderReprocessInfo>(DEFAULT_REPROCESS_INFO);
  readonly workflowData = signal<WorkflowUserApproverModel | null>(null);
  readonly createWorkflowLoading = signal(false);

  readonly simulateDfdlOrder = signal<string | null>(null);
  readonly simulateDfdlBilling = signal<string | null>(null);

  readonly realtimeData = signal<unknown | null>(null);
  readonly realtimeLoading = signal(false);
  readonly realtimeError = signal<string | null>(null);
  readonly realtimeReady = computed(
    () => !this.realtimeLoading() && this.realtimeData() !== null
  );
  callNumber = '';

  readonly hasData = computed(() => this.data() !== null);
  readonly showApprovalWorkflow = computed(() => this.workflowData() !== null);

  readonly canSeeOrderV2 = (): boolean => this.authService.hasRole(ROLE_VTEX_ORDERS_WRITE);
  readonly canPerformWorkflowActions = computed(() => {
    const w = this.workflowData();
    if (!w?.allowedRoles?.length) return true;
    return this.authService.hasAnyRole(w.allowedRoles);
  });

  constructor() {
    effect(() => {
      const orderId = this.routeOrderId();
      if (!orderId) return;
      this.loadOrder(orderId);
      this.loadTimeline(orderId);
      this.loadWorkflow(orderId);
      this.loadDfdlSimulations(orderId);
      this.loadRealtime(orderId);
    });
  }

  private loadRealtime(orderId: string): void {
    this.realtimeLoading.set(true);
    this.realtimeError.set(null);
    this.realtimeData.set(null);

    this.ordersService
      .realtime(orderId)
      .pipe(finalize(() => this.realtimeLoading.set(false)))
      .subscribe({
        next: (res) => this.realtimeData.set(res),
        error: (err) =>
          this.realtimeError.set(err?.error?.message ?? 'Falha ao carregar realtime.'),
      });
  }

  openRealtimeModal(): void {
    if (!this.realtimeReady()) return;
    this.realtimeModal?.open();
  }

  private loadOrder(orderId: string): void {
    this.loading.set(true);
    this.error.set(null);
    this.data.set(null);

    this.ordersService.detailV2(orderId).subscribe({
      next: (response) => {
        try {
          this.data.set(mapOrderV2ApiToData(orderId, response));
        } catch (err) {
          this.error.set(err instanceof Error ? err.message : 'Falha ao processar o pedido.');
        } finally {
          this.loading.set(false);
        }
      },
      error: (err) => {
        this.error.set(err?.error?.message ?? 'Falha ao carregar o pedido.');
        this.loading.set(false);
      },
    });
  }

  private loadTimeline(orderId: string): void {
    this.timelineLoading.set(true);
    this.timeline.set([]);
    this.kruzerTimeline.set([]);
    this.reprocess.set(DEFAULT_REPROCESS_INFO);

    this.orderTimelineService.getTimelineEnvelope(orderId).subscribe({
      next: (envelope) => {
        const tl = envelope.orderTimelineEvents;
        this.timeline.set(this.toTimelineEvents(tl.occurredEvents, tl.expectedEvents));
        this.kruzerTimeline.set(this.toKruzerTimelineEvents(envelope.kruzer?.occurredEvents ?? []));
        this.applyAlerts(envelope.alerts ?? []);
        this.reprocess.set(envelope.reprocess ?? DEFAULT_REPROCESS_INFO);
        this.timelineLoading.set(false);
      },
      error: () => {
        this.timeline.set([]);
        this.kruzerTimeline.set([]);
        this.applyAlerts([]);
        this.reprocess.set(DEFAULT_REPROCESS_INFO);
        this.timelineLoading.set(false);
      },
    });
  }

  private loadWorkflow(orderId: string): void {
    this.workflowData.set(null);
    this.workflowOrderService
      .query(orderId)
      .pipe(
        catchError(() => of(null))
      )
      .subscribe((workflow) => {
        if (workflow) this.workflowData.set(workflow);
      });
  }

  private loadDfdlSimulations(orderId: string): void {
    this.simulateDfdlOrder.set(null);
    this.simulateDfdlBilling.set(null);

    this.ordersService.getDfdlOrder(orderId).pipe(catchError(() => of(null))).subscribe((res) => {
      if (res) {
        const parsed = this.dfdlOrderToJson(res);
        this.simulateDfdlOrder.set(JSON.stringify(parsed, null, 2));
      }
    });

    this.ordersService.getDfdlBilling(orderId).pipe(catchError(() => of(null))).subscribe((res) => {
      if (res) {
        const parsed = this.dfdlOrderToJson(res);
        this.simulateDfdlBilling.set(JSON.stringify(parsed, null, 2));
      }
    });
  }

  private applyAlerts(alerts: OrderAlert[]): void {
    this.data.update((current) => (current ? { ...current, alerts } : current));
  }

  openReprocessModal(): void {
    if (this.workflowData()) return;
    this.reprocessModal.openDialog();
  }

  confirmReprocess(): void {
    if (this.workflowData()) return;
    if (this.createWorkflowLoading()) return;

    const orderId = this.routeOrderId();
    if (!orderId) return;

    this.createWorkflowLoading.set(true);
    this.workflowOrderService
      .create(orderId)
      .pipe(finalize(() => this.createWorkflowLoading.set(false)))
      .subscribe({
        next: (workflow) => {
          this.reprocessModal.closeDialog();
          this.workflowData.set(workflow);
          this.successMsg.set('Workflow criado com sucesso.');
          this.error.set(null);
        },
        error: (err) => {
          const msg =
            err?.error?.message ||
            err?.message ||
            'Não foi possível criar o workflow. Tente novamente.';
          this.error.set(msg);
        },
      });
  }

  onWorkflowUpdated(workflow: WorkflowUserApproverModel | null): void {
    this.workflowData.set(workflow);
  }

  onWorkflowCancelled(): void {
    this.workflowData.set(null);
  }

  private toTimelineEvents(
    occurred: OrderTimelineEvent[],
    expected: OrderTimelineEvent[]
  ): TimelineEvent[] {
    const occurredList = occurred ?? [];
    const expectedList = expected ?? [];

    const occurredMapped: TimelineEvent[] = occurredList.map((e) => ({
      from: displaySystemName(e.fromSystemName),
      to: displaySystemName(e.toSystemName),
      isHook: HOOK_OCCURRED.has(e.fromSystemName),
      description: e.description,
      occurredAt: this.formatDate(e.date),
      status: 'done',
    }));

    let pendingCurrentIndex: number | null = null;
    const expectedMapped: TimelineEvent[] = expectedList.map((e, index) => {
      const isKruzer = e.fromSystemName === 'Kruzer';
      let status: TimelineEvent['status'] = 'disabled';

      if (index === 0) {
        status = isKruzer ? 'err' : 'warn';
        pendingCurrentIndex = index;
      }

      const lastPendingItem =
        pendingCurrentIndex !== null ? expectedList[pendingCurrentIndex] : undefined;

      if (lastPendingItem?.description === PAYMENT_APPROVED_DESC) {
        if (e.description === PAYMENT_APPROVED_DESC) {
          status = 'warn';
        }

        const lastNextPendingItem = expectedList[(pendingCurrentIndex as number) + 1];
        const nextNextItem = expectedList[(pendingCurrentIndex as number) + 2];

        const isKruzerNextNext =
          isKruzer &&
          nextNextItem?.fromSystemName === e.fromSystemName &&
          nextNextItem?.toSystemName === e.toSystemName &&
          nextNextItem?.description === e.description &&
          nextNextItem?.name === e.name &&
          nextNextItem?.date === e.date;

        if (isKruzerNextNext && lastNextPendingItem?.description === PAYMENT_APPROVED_DESC) {
          status = 'err';
        }
      }

      const isAfterCurrent =
        pendingCurrentIndex !== null && index === pendingCurrentIndex + 1;

      if (pendingCurrentIndex !== null && isAfterCurrent && isKruzer) {
        status = 'err';
      }

      return {
        from: displaySystemName(e.fromSystemName),
        to: displaySystemName(e.toSystemName),
        isHook: HOOK_EXPECTED.has(e.fromSystemName),
        description: e.description,
        occurredAt: '',
        status,
      };
    });

    return [...occurredMapped, ...expectedMapped];
  }

  private toKruzerTimelineEvents(occurred: KruzerOrderTimelineEvent[]): TimelineEvent[] {
    return (occurred ?? []).map((e) => ({
      from: displaySystemName(e.fromSystemName),
      to: displaySystemName(e.toSystemName),
      isHook: false,
      description: e.description ?? '',
      occurredAt: this.formatDate(e.date),
      status: 'done',
      payload: e.payload ?? null,
    }));
  }

  private formatDate(iso: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const pad = (n: number) => n.toString().padStart(2, '0');
    return (
      `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ` +
      `${pad(d.getHours())}:${pad(d.getMinutes())}`
    );
  }

  private dfdlOrderToJson(payload: any, hideBlankValues = true): any {
    const results: any = {};
    if (typeof payload !== 'string') return results;
    const lines = payload.split('\r');

    for (const line of lines) {
      const tagMatch = line.match(/^\[([A-Z]+)\]/);
      if (!tagMatch) continue;

      const tag = `[${tagMatch[1]}]`;
      const content = line.replace(/^\[[A-Z]+\]/, '');
      const values = content.split('|');

      const schema = this.schemas[tag];
      if (!schema) {
        results[tag] = results[tag] || [];
        results[tag].push({ raw: values });
        continue;
      }

      const mapped: any = {};
      for (let i = 0; i < schema.length; i++) {
        if (hideBlankValues) {
          if (values[i]) mapped[schema[i]] = values[i] || 'BRANCO';
        } else {
          mapped[schema[i]] = values[i] || 'BRANCO';
        }
      }
      results[tag] = results[tag] || [];
      results[tag].push(mapped);
    }
    return results;
  }

  private schemas: Record<string, string[]> = {
    '[CLI]': ['CustomerID','CPF','CNPJ','OtherDoc','DocumentType','CustomerName','Email','BirthDay','StreetName','AdressName','AdressNumber','Complement','StreetType','District','City','ZipCode','State','Country','HomePhone','MobilePhone','SendEmail','SintegraSituationDate','SintegraCodeSituation','SuframaNumber','SuframaSituation','CNAENumber','FederalRevenueStatus','FederalRevenueStatusDate','SimpleCompany','FederalRevenueStatusDescription','EconomicActiveDescription','IBGECode','SintegraSituation','Gender','Nome Social','001','002','003','004','005','006'],
    '[ORD]': ['OrderID','ClientOrderID','SystemSource','OrderDateTime','OrderSubtotalAmount','ShippingFreightValue','OrderTotalShip','InterestTotal','DiscountTotal','OrderTotalAmount','OrderTotalItems','ShippingMethod','RecepientName','QuantityMethodPayment','DeliverAddressAddressName','DeliverAddressStreetName','DeliverAddressNumber','DeliverAddressComplement','DeliverAddressDistrict','DeliverAddressCity','DeliverAddressZipCode','DeliverAddressState','DeliverAddressCountry','DeliverAddressStreetType','DeliveryPhone','DeliveryMobilePhone','IsWeddingList','WeddingListID','CFNumber','GiftCard','ColorInformationPackage','SmileID','CommitteeReturn','DeliveryRestrictionCode','HotSiteID','HotSiteDescription','InventoryLocationID','PricingTableId','ScheduledOrderDate','ScheduleDeliveryPeriod','ScheduleDeliveryPeriodID','ShippingEstimatedShippingDays','TicketMaturityTerm','VirtualPartnerID','VirtualPartnerControl','ShippingDate','SystemCalcDate','SessionId','SellerId','SellerName','PricingTableInitialId','OrderIDProvider','DiscountAuthorizer','DiscountReason','IDParceiroMarketPlace','NomeParceiroMarketPlace','DaysType','PeriodoDe','PeriodoAte','PayAgentRate','IntegratingAgentRate','OrderIDPack','OrderIDOdd','PrizeChannel','isPlanSignBenefit','me2token','client_scr','prioritized','Observation','ShipLabelIDPartner','CashBack_ReservationId','CashBack_Value','SalesChannel','PrimePlan','OrderOrigin','OrderEmail','AffiliateId','AccountName','SellerOrderID','MultipleShipments','FutureSale'],
    '[PG]': ['BillingAddressName','BillingStreetName','BillingStreetType','BillingNumber','BillingComplement','BillingDistrict','BillingCity','BillingZipCode','BillingState','BillingCountry','BillingMultipePayment'],
    '[MP]': ['SendEmail'],
    '[PGL]': ['SequencePaymentID','BillingMethodID','BillingMethodPayValue','FlagCardID','InstallmentQuantity','InstallmentValue','BillingURLBoleto','BillingLinhaDig','BilletId','paymentSystemVTEX','Paymentsystem','PaymentSystemName','PaymentIdVTEX','BillingBarCodeNumber','PaymentGroupVTEX','PaymentRedemptionCode'],
    '[ITE]': ['SKU','ProductID','PrincipalProductID','ItemName','ItemCategory','Quantity','UnitPrice','Interest','Discount','TotalPrice','ProductBundle','ServiceType','ProductRelated','MobiliyPlanCode','MobilityPlanName','MobileOperator','PlanType','Others','isSubsidized','MonthlyPayment','isTriangulated','PlanCharacteristic','ProductM1','ProductLine','UnitPriceOpen','CashBackValue','UnitPriceWithDiscount','ModalityType','ProductReleaseDate','ServiceLine','ServiceCategory','ServiceSubCategory','IdSkuVTEX','AccountNameSeller','StockLocation','DockIdVTEX','BundleId','skuProduto','marca','modelo','lmi'],
    '[STO]': ['RecordShopID','PurchaseType','ManagerID','SellerID','PriceTableID','SubPriceTableID','LCCode','AffiliateInvoiceVF','CPFNumber','OtherDoc'],
    '[NFL]': ['InvoiceVF','SerieInvoiceVF','SKUInvoice','SKUQuantityReturned','ValueItemsReturned','AffiliateInvoiceVF','GiverListCode','LstLejour','IDLejour'],
    '[SHP]': ['Período','Tipo de Entrega','Tipo de correio','Leadtime da entrega','Data Entrega','Código Transportadora GAN','Custo Frete','Dias de Transferência','Filial estoque ressuprimento'],
    '[OPEN]': ['OPEN_Type','OPEN_DataString'],
    '[H]': ['Program','Operation','User','Datetime','Version'],
    '[BIL]': ['ORDERID','CLIENTORDERID','SISTEMASOURCE','ORDERDATETIME','TOTALAMOUNT','PLOTSNUMBER','BILLINGSTATUS','TNSUNUMBER','AUTHORIZATIONNUMBER','OPERATIONCARDCODE','PAYMENTMETHODCODE','PAYMENTMETHOD','BUYERCODE','PHONENUMBER','CONTRACTNUMBER','INDEX','REASONCODE','REASONDESCRIPTION','PIXCODE','AUTHORIZATIONDATE','ESTABLISHMENT','TERMINAL','PRIORITIZED','PRIORTYPE','BINCARD','PAYMENTGROUP','PAYMENTSYSTEM','PAYMENTSYSTEMNAME','GIFTCARDID','MethodID','RedemptionCode','SPLIT','Period','DeliveryType','DeliveryCorrier','LeadTime','DeliveryDate','DeliveryCarrier','ShippingCost','TransferredDaysResupply','StoreIdResupply'],
  };
}
